/**
 * SearchableSelect — typeahead-filtered combobox.
 *
 * Replaces native <select> for long lists where users want to type to narrow.
 * For short categorical pickers (3-5 options like direction / status filters)
 * keep using the native Select — search is overhead for those.
 *
 * Behavior:
 *   - Type to filter the list (case-insensitive substring match on label)
 *   - Up/Down arrows move highlight; Enter selects; Esc closes
 *   - Click outside closes
 *   - Empty value clears selection (controlled — caller owns state)
 *
 * The component intentionally does NOT support multi-select, grouping, or
 * remote search. Those are real features but each adds complexity that
 * isn't needed yet in this codebase; ship the simple thing.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface SearchableSelectItem {
  value: string;
  label: string;
  /** Optional secondary line shown beneath the label in the dropdown. */
  hint?: string;
  /** Disabled items render greyed and can't be selected. */
  disabled?: boolean;
}

export interface SearchableSelectProps {
  items: SearchableSelectItem[];
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Optional id for label association (use with FormField's auto-id). */
  id?: string;
  /** Shown when filter matches nothing. */
  emptyMessage?: string;
  /** Show an X to clear the current selection (controlled by `value`). */
  clearable?: boolean;
  className?: string;
}

export function SearchableSelect({
  items,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  invalid = false,
  id,
  emptyMessage = 'No matches',
  clearable = false,
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Panel position in viewport coords. Patch 5+ audit: the panel must
  // render outside the modal's scroll container, otherwise it gets clipped
  // by overflow-y-auto. We portal to document.body and use position:fixed
  // with measured coords. Re-measured on open + on viewport resize.
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );

  const selected = useMemo(() => items.find((i) => i.value === value) ?? null, [items, value]);

  // Filtered items — substring match, case-insensitive, preserves order.
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (f === '') return items;
    return items.filter(
      (i) =>
        i.label.toLowerCase().includes(f) ||
        (i.hint ? i.hint.toLowerCase().includes(f) : false),
    );
  }, [items, filter]);

  // Reset highlight to first enabled item whenever the filter changes.
  useEffect(() => {
    const first = filtered.findIndex((i) => !i.disabled);
    setHighlight(first === -1 ? 0 : first);
  }, [filter, filtered]);

  // Measure trigger position whenever the panel opens. useLayoutEffect so
  // the first frame the panel renders with correct coords (no flash).
  useLayoutEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }
    function measure() {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      setPanelPos({
        top: rect.bottom + 4, // 4px gap below the trigger
        left: rect.left,
        width: rect.width,
      });
    }
    measure();
    window.addEventListener('resize', measure);
    // Close on scroll anywhere (cheap + matches what users expect — the
    // panel detaching from its trigger when content moves is jarring).
    function onScroll() {
      setOpen(false);
      setFilter('');
    }
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // Close on outside click. Patch 5+ audit: with the panel portaled, we
  // must check BOTH the root (containing the trigger) AND the panel itself,
  // since they're no longer in the same DOM subtree.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
      setFilter('');
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Focus the search input when the dropdown opens.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const commit = useCallback(
    (item: SearchableSelectItem) => {
      if (item.disabled) return;
      onChange(item.value);
      setOpen(false);
      setFilter('');
    },
    [onChange],
  );

  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => {
        // Move to next enabled item, wrapping.
        for (let i = 1; i <= filtered.length; i++) {
          const next = (h + i) % filtered.length;
          if (!filtered[next]?.disabled) return next;
        }
        return h;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => {
        for (let i = 1; i <= filtered.length; i++) {
          const next = (h - i + filtered.length) % filtered.length;
          if (!filtered[next]?.disabled) return next;
        }
        return h;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filtered[highlight];
      if (target) commit(target);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setFilter('');
    }
  };

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'flex h-9 w-full items-center justify-between rounded-md border bg-surface px-3 pr-8 text-left text-sm text-primary disabled:bg-subtle disabled:text-tertiary disabled:cursor-not-allowed',
          invalid ? 'border-status-danger' : 'border-border-default hover:border-border-strong',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={cn('truncate', !selected && 'text-tertiary')}>
          {selected ? selected.label : placeholder}
        </span>
      </button>
      {/* Clear (X) or chevron icon */}
      {clearable && selected && !disabled ? (
        <button
          type="button"
          aria-label="Clear selection"
          onClick={(e) => {
            e.stopPropagation();
            onChange(null);
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-tertiary hover:bg-subtle hover:text-secondary"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      ) : (
        <ChevronDown
          className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tertiary"
          strokeWidth={1.5}
        />
      )}

      {/* Dropdown panel — portaled to document.body so it escapes parent
          overflow:hidden / scroll containers and stacking contexts. Without
          the portal, the panel gets clipped when the SearchableSelect is
          rendered inside a scrollable modal body. */}
      {open && panelPos
        ? createPortal(
            <div
              ref={panelRef}
              // z-[60] sits above Modal/Drawer's z-50 backdrop.
              className="fixed z-[60] rounded-md border border-border-default bg-surface shadow-[var(--shadow-overlay)]"
              style={{ top: panelPos.top, left: panelPos.left, width: panelPos.width }}
            >
              <div className="border-b border-border-default p-1.5">
                <input
                  ref={inputRef}
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Search…"
                  className="w-full rounded-sm bg-base px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:outline-none"
                />
              </div>
              <ul
                role="listbox"
                className="max-h-64 overflow-y-auto py-1"
                aria-label={placeholder}
              >
                {filtered.length === 0 ? (
                  <li className="px-3 py-2 text-xs text-tertiary">{emptyMessage}</li>
                ) : (
                  filtered.map((item, idx) => (
                    <li
                      key={item.value}
                      role="option"
                      aria-selected={item.value === value}
                      aria-disabled={item.disabled}
                      onMouseEnter={() => setHighlight(idx)}
                      onClick={() => commit(item)}
                      className={cn(
                        'cursor-pointer px-3 py-1.5 text-sm',
                        item.disabled
                          ? 'cursor-not-allowed text-tertiary'
                          : idx === highlight
                            ? 'bg-accent-subtle text-primary'
                            : 'text-primary hover:bg-subtle',
                        item.value === value && 'font-medium',
                      )}
                    >
                      <div className="truncate">{item.label}</div>
                      {item.hint ? (
                        <div className="truncate text-xs text-secondary">{item.hint}</div>
                      ) : null}
                    </li>
                  ))
                )}
              </ul>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
